/** Release the pinned broadcast-channel transport when a Redux store is replaced. */
import { chooseMethod } from "broadcast-channel/dist/lib/method-chooser";
import { fillOptionsWithDefaults } from "broadcast-channel/dist/lib/options";
import { createActiveStateSyncScope } from "./active-state-sync";

export function createManagedStateSyncScope(excludedTypes, transport) {
  const scope = createActiveStateSyncScope(excludedTypes);
  const underlying = transport || chooseMethod(fillOptionsWithDefaults({}));
  let active = true;
  let prepared;
  let closing;
  const pending = new Set();
  const managed = {
    ...underlying,
    type: `code10-managed-${underlying.type}`,
    create(name, options) {
      prepared = Promise.resolve(underlying.create(name, options));
      return prepared;
    },
    postMessage(state, message) {
      if (!active) return Promise.resolve();
      const current = Promise.resolve().then(() => {
        if (active) return underlying.postMessage(state, message);
      });
      pending.add(current);
      current.then(
        () => pending.delete(current),
        () => pending.delete(current)
      );
      return current;
    },
    onMessage(state, callback, ...rest) {
      return underlying.onMessage(state, active ? callback : null, ...rest);
    },
  };
  return {
    predicate: scope.predicate,
    broadcastChannelOption: { methods: [managed] },
    deactivate() {
      if (closing) return closing;
      active = false;
      scope.deactivate();
      closing = (async () => {
        if (!prepared) return;
        const state = await prepared;
        underlying.onMessage(state, null);
        await Promise.allSettled(Array.from(pending));
        // Native wrappers can remain referenced by a previous store. Detach
        // their callback and release the native object after a successful close.
        const native = underlying.type === "native" ? state.bc : null;
        if (native) native.onmessage = null;
        await underlying.close(state);
        if (native) state.bc = null;
      })();
      return closing;
    },
  };
}
